import {
  BaseApiService,
  type JsonApiResource,
  type JsonApiResponse,
} from '@/shared/api/services/base.service';
import type { PaymentMethodData } from '@/types/stripe';

export type PaymentMethodResource = JsonApiResource<PaymentMethodData>;

class PaymentMethodsService extends BaseApiService {
  async list(): Promise<PaymentMethodData[]> {
    const response = await this.httpGet<JsonApiResponse<PaymentMethodResource[]>>(
      '/v1/payment-methods'
    );
    return this.normalizeMany(response);
  }

  async setDefault(id: string): Promise<PaymentMethodData> {
    const response = await this.patch<JsonApiResponse<PaymentMethodResource>>(
      `/v1/payment-methods/${id}`,
      this.formatJsonApiData('default', { is_default: true }, undefined, id)
    );
    return this.normalizeOne(response);
  }

  async remove(id: string): Promise<void> {
    await this.httpDelete(`/v1/payment-methods/${id}`);
  }

  async createSetupCheckout(
    returnUrl?: string,
    metadata?: Record<string, string>
  ): Promise<{
    checkout_url: string;
    setup_mode: boolean;
    payment_id?: string;
  }> {
    const response = await this.post<
      JsonApiResponse<
        JsonApiResource<{
          checkout_url: string;
          setup_mode: boolean;
          payment_id?: string;
        }>
      >
    >(
      '/v1/payment-methods/setup-checkout',
      this.formatJsonApiData('setup-checkout', {
        return_url: returnUrl,
        metadata,
      })
    );
    return response.data.attributes;
  }

  async checkSetupPaymentStatus(paymentId: string): Promise<{
    payment_verified: boolean;
    payment_processing: boolean;
    status: string;
    metadata?: Record<string, unknown>;
  }> {
    const response = await this.httpGet<
      JsonApiResponse<
        JsonApiResource<{
          status: string;
          type: string;
          metadata?: Record<string, unknown>;
        }>
      >
    >(`/v1/payments/${paymentId}`);
    const status = response.data.attributes.status;
    return {
      payment_verified: status === 'completed' || status === 'succeeded',
      payment_processing: status === 'processing' || status === 'pending',
      status,
      metadata: response.data.attributes.metadata,
    };
  }
}

export const paymentMethodsService = new PaymentMethodsService();
